import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type { IHttpService } from "App/Modules/Services";

import type {
    ICompanySlim,
    IDataRetention,
    IHoliday,
    IHolidaySchedule,
    ILabel,
    ILanguage,
    IPartner,
    IPartnerInsert,
    IPartnerSlim,
    IResponse,
    ISiteSlim,
} from "../../Models";
import { Permission } from "../../Models";
import type { IPartnersRepository } from "./IPartnersRepository";

export default class PartnersRepository implements IPartnersRepository {
    public static $inject = ["mmHttpService"];

    constructor(private readonly httpService: IHttpService) {}

    public getPartners(): Observable<IResponse<IPartnerSlim[]>> {
        return this.httpService.get<IPartnerSlim[]>("partners");
    }

    public getPartnersIncludingGlobal(): Observable<IResponse<IPartnerSlim[]>> {
        return this.httpService.get<IPartnerSlim[]>("partners/includingGlobal");
    }

    public getPartner(partnerId: string): Observable<IResponse<IPartner>> {
        return this.httpService.get<IPartner>(`partners/${partnerId}`);
    }

    public getPartnerAuthorizedPermissions(partnerId: string): Observable<IResponse<Permission[]>> {
        return this.httpService.get<Permission[]>(`partners/${partnerId}/authorizedPermissions`).pipe(
            map((response: IResponse<Permission[]>): IResponse<Permission[]> => {
                if (response.data) {
                    response.data = response.data.map((p) => (Permission as any)[p] as Permission);
                    return response;
                }
                return response;
            }),
        );
    }

    public getPartnerLanguages(partnerId: string): Observable<IResponse<ILanguage[]>> {
        return this.httpService.get<ILanguage[]>(`partners/${partnerId}/languages`);
    }

    public getPartnerCompanies(partnerId: string): Observable<IResponse<ICompanySlim[]>> {
        return this.httpService.get<ICompanySlim[]>(`partners/${partnerId}/companies`);
    }

    public getPartnerSites(partnerId: string): Observable<IResponse<ISiteSlim[]>> {
        return this.httpService.get<ISiteSlim[]>(`partners/${partnerId}/sites`);
    }

    public getSiteLabels(partnerId: string): Observable<IResponse<ILabel[]>> {
        return this.httpService.get<ILabel[]>(`partners/${partnerId}/siteLabels`);
    }

    public addPartner(partner: IPartnerInsert): Observable<IResponse<IPartner>> {
        return this.httpService.post<IPartner>("partners", partner);
    }

    public patch(partnerId: string, partner: IPartner): Observable<IResponse<IPartner>> {
        this.removeUnsupportedPatchProperties(partner);
        return this.httpService.patch<IPartner>(`partners/${partnerId}`, partner);
    }

    public getPartnerLabels(partnerId: string): Observable<IResponse<ILabel[]>> {
        return this.httpService.get<ILabel[]>(`partners/${partnerId}/users/labels`);
    }

    public removeLabel(partnerId: string, labelId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`partners/${partnerId}/users/labels/${labelId}`);
    }

    public editLabel(partnerId: string, editedLabel: ILabel): Observable<IResponse<ILabel>> {
        return this.httpService.patch(`partners/${partnerId}/users/labels/${editedLabel.id}`, editedLabel);
    }

    public replaceLabel(partnerId: string, mergeId: string, removeId: string) {
        return this.httpService.delete(`partners/${partnerId}/users/labels/merge/${removeId}/with/${mergeId}`);
    }

    public createPartnerHolidays(
        partnerId: string,
        holidaySchedule: IHolidaySchedule,
    ): Observable<IResponse<IHolidaySchedule>> {
        return this.httpService.post(`partners/${partnerId}/holidaySchedule`, holidaySchedule);
    }

    public patchPartnerHolidays(
        partnerId: string,
        holidaySchedule: IHolidaySchedule | IHoliday[],
    ): Observable<IResponse<IHolidaySchedule>> {
        return this.httpService.patch(`partners/${partnerId}/holidaySchedule`, holidaySchedule);
    }

    public addDataRetention(partnerId: string, dataRetention: IDataRetention): Observable<IResponse<IDataRetention>> {
        return this.httpService.post<IDataRetention>(`partners/${partnerId}/dataRetentions`, dataRetention);
    }

    public patchDataRetention(
        partnerId: string,
        dataRetentionId: string,
        dataRetention: IDataRetention,
    ): Observable<IResponse<IDataRetention>> {
        return this.httpService.patch<IDataRetention>(
            `partners/${partnerId}/dataRetentions/${dataRetentionId}`,
            dataRetention,
        );
    }

    public deletePartnerLogo(partnerId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`partners/${partnerId}/logo`);
    }

    private removeUnsupportedPatchProperties(partner: IPartner): void {
        if (partner.holidaySchedule) {
            delete partner.holidaySchedule;
        }

        if (partner.dataRetention) {
            delete partner.dataRetention;
        }
    }
}

module("mmRepositories").service("mmPartnersRepository", PartnersRepository);
